import React, { useState, useEffect, useCallback } from "react";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Bell, Send, Clock, Plus, Trash2, Edit, CheckCircle2,
  AlertTriangle, Megaphone, Users,
  Trophy, Upload, FileUp, Loader2, Eye, Pencil,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import PinDialog from "@/components/PinDialog";

import { getSourceLabel } from "@/lib/labels";
import PickleagueTab from "@/components/PickleagueTab";
import TournamentCRM from "@/components/tournament/TournamentCRM";

export default function NotificationsPage() {
  const [activeTab, setActiveTab] = useState("send");
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logStats, setLogStats] = useState({ total_sent: 0, total_failed: 0, zns_template_ids: [], sources: [] });
  const [logFilterSource, setLogFilterSource] = useState("");
  const [logFilterZns, setLogFilterZns] = useState("");
  const [logFilterStatus, setLogFilterStatus] = useState("");
  const [znsLogDetail, setZnsLogDetail] = useState(null);
  const [znsLogLoading, setZnsLogLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [allSources, setAllSources] = useState([]);
  const [alerts, setAlerts] = useState({ expired_members: [], expiring_soon_members: [] });
  const [loading, setLoading] = useState(true);

  // Send tab state
  const [channel, setChannel] = useState("zalo");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedModes, setSelectedModes] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [zaloStatus, setZaloStatus] = useState({ connected: false, hasTemplate: false });

  // Template modal
  const [tmplModalOpen, setTmplModalOpen] = useState(false);
  const [editingTmpl, setEditingTmpl] = useState(null);

  // CRM giải đấu
  const [tZnsTemplateId, setTZnsTemplateId] = useState("");

  // Recipient preview popup
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState([]);

  // Draft preview
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftPreviews, setDraftPreviews] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);

  const handleDraftPreview = async () => {
    if (!selectedTemplate) { toast.error("Vui lòng chọn mẫu tin nhắn"); return; }
    const ids = getRecipientIds();
    if (ids.length === 0) { toast.error("Vui lòng chọn người nhận"); return; }
    setDraftLoading(true);
    try {
      const { data } = await api.post("/notifications/draft-preview", { template_id: selectedTemplate.id, customer_ids: ids });
      setDraftPreviews(data.previews || []);
      setDraftOpen(true);
    } catch (err) { toast.error("Lỗi tạo bản nháp"); }
    finally { setDraftLoading(false); }
  };

  const toggleMode = (mode) => {
    setSelectedModes(prev => prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]);
  };

  const handleRecipientDoubleClick = (mode) => {
    // Collect IDs for this specific mode
    let ids = [];
    if (mode === "all") ids = filteredBySourceCustomers.map(c => c.id);
    else if (mode === "expiring") ids = filteredExpiringIds;
    else if (mode === "expired") ids = filteredExpiredIds;
    else if (mode === "paid") ids = filteredPaidIds;
    else if (mode === "unpaid") ids = filteredUnpaidIds;
    else if (mode === "overdue_pay") ids = filteredOverdueIds;
    else if (mode === "manual") ids = selectedCustomerIds;
    const seen = new Set();
    const list = [];
    for (const id of ids) {
      const c = customers.find(x => x.id === id);
      if (c) { const key = c.phone || id; if (!seen.has(key)) { seen.add(key); list.push(c); } }
    }
    setPreviewRecipients(list);
    if (list.length > 0) setPreviewOpen(true);
  };
  const [tContent, setTContent] = useState("");
  const [tFee, setTFee] = useState("");
  const [tBankAccount, setTBankAccount] = useState("");
  const [tDeadline, setTDeadline] = useState("");
  const [tPhones, setTPhones] = useState("");
  const [tSending, setTSending] = useState(false);
  const [tResult, setTResult] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, aRes] = await Promise.all([
        api.get("/notifications/templates"),
        api.get("/groups/alerts"),
      ]);
      setTemplates(tRes.data.templates || []);
      setAlerts(aRes.data || { expired_members: [], expiring_soon_members: [] });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  // Lazy load customers only when send tab is active
  const customersLoadedRef = React.useRef(false);
  useEffect(() => {
    if (activeTab === "send" && !customersLoadedRef.current) {
      customersLoadedRef.current = true;
      api.get("/customers").then(({ data }) => {
        setCustomers(data.customers || []);
        setAllSources(data.all_sources || []);
      }).catch(() => {});
    }
  }, [activeTab]);

  const logPageRef = React.useRef(1);
  const logsLoadingRef = React.useRef(false);
  const hasMoreRef = React.useRef(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const logsEndRef = React.useRef(null);

  const loadLogs = useCallback(async (append = false) => {
    if (logsLoadingRef.current) return;
    logsLoadingRef.current = true;
    setLogsLoading(true);
    try {
      const pg = append ? logPageRef.current + 1 : 1;
      let url = `/notifications/logs?page=${pg}&limit=30`;
      if (logFilterSource) url += `&source=${encodeURIComponent(logFilterSource)}`;
      if (logFilterZns) url += `&zns_tid=${encodeURIComponent(logFilterZns)}`;
      if (logFilterStatus) url += `&status=${encodeURIComponent(logFilterStatus)}`;
      const { data } = await api.get(url);
      const newLogs = data.logs || [];
      if (append) {
        setLogs(prev => {
          const combined = [...prev, ...newLogs];
          hasMoreRef.current = combined.length < (data.total || 0);
          return combined;
        });
      } else {
        setLogs(newLogs);
        hasMoreRef.current = newLogs.length < (data.total || 0);
      }
      logPageRef.current = pg;
      setLogPage(pg);
      setLogTotal(data.total || 0);
      setLogStats(data.stats || { total_sent: 0, total_failed: 0, zns_template_ids: [], sources: [] });
    } catch (e) { console.error(e); }
    finally { logsLoadingRef.current = false; setLogsLoading(false); }
  }, [logFilterSource, logFilterZns, logFilterStatus]);


  const viewZnsLogDetail = async (logId) => {
    setZnsLogLoading(true);
    try {
      const { data } = await api.get(`/notifications/logs/${logId}`);
      setZnsLogDetail(data);
    } catch { toast.error("Lỗi tải chi tiết log"); }
    finally { setZnsLogLoading(false); }
  };


  // Infinite scroll observer
  useEffect(() => {
    if (activeTab !== "logs") return;
    const el = logsEndRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !logsLoadingRef.current && hasMoreRef.current) {
        loadLogs(true);
      }
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, loadLogs, logTotal]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (activeTab === "logs") loadLogs(); }, [activeTab, loadLogs]);

  // Check Zalo connection status
  useEffect(() => {
    const checkZalo = async () => {
      try {
        const [connRes, settingsRes] = await Promise.all([
          api.get("/notifications/zalo/test-connection").catch(() => ({ data: { success: false } })),
          api.get("/notifications/settings").catch(() => ({ data: { settings: {} } })),
        ]);
        const s = settingsRes.data.settings || {};
        const hasTemplate = !!(s.zns_template_nhac_gia_han || s.zns_template_xac_nhan_dat_san || s.zns_template_khuyen_mai);
        setZaloStatus({ connected: connRes.data.success, hasTemplate });
      } catch (e) { /* ignore for non-CEO */ }
    };
    checkZalo();
  }, []);

  const expiringIds = alerts.expiring_soon_members.map(m => m.customer_id).filter(Boolean);
  const expiredIds = alerts.expired_members.map(m => m.customer_id).filter(Boolean);

  // Payment-status based lists from customer data
  const paidCustomerIds = customers.filter(c => c.payment_status === "paid").map(c => c.id);
  const unpaidCustomerIds = customers.filter(c => c.payment_status === "unpaid").map(c => c.id);
  const overdueCustomerIds = customers.filter(c => c.payment_status === "overdue").map(c => c.id);

  // Apply source filter on top of customers
  const filteredBySourceCustomers = selectedSources.length > 0
    ? customers.filter(c => selectedSources.includes(c.source))
    : customers;
  const filteredBySourceIds = new Set(filteredBySourceCustomers.map(c => c.id));
  const filteredExpiringIds = expiringIds.filter(id => selectedSources.length === 0 || filteredBySourceIds.has(id));
  const filteredExpiredIds = expiredIds.filter(id => selectedSources.length === 0 || filteredBySourceIds.has(id));
  const filteredPaidIds = paidCustomerIds.filter(id => selectedSources.length === 0 || filteredBySourceIds.has(id));
  const filteredUnpaidIds = unpaidCustomerIds.filter(id => selectedSources.length === 0 || filteredBySourceIds.has(id));
  const filteredOverdueIds = overdueCustomerIds.filter(id => selectedSources.length === 0 || filteredBySourceIds.has(id));

  const getRecipientIds = () => {
    const allIds = new Set();
    for (const mode of selectedModes) {
      let ids = [];
      if (mode === "all") ids = filteredBySourceCustomers.map(c => c.id);
      else if (mode === "expiring") ids = filteredExpiringIds;
      else if (mode === "expired") ids = filteredExpiredIds;
      else if (mode === "paid") ids = filteredPaidIds;
      else if (mode === "unpaid") ids = filteredUnpaidIds;
      else if (mode === "overdue_pay") ids = filteredOverdueIds;
      else if (mode === "manual") ids = selectedCustomerIds;
      ids.forEach(id => allIds.add(id));
    }
    // Dedupe by phone
    const seen = new Set();
    const deduped = [];
    for (const id of allIds) {
      const c = customers.find(x => x.id === id);
      const key = c?.phone || id;
      if (!seen.has(key)) { seen.add(key); deduped.push(id); }
    }
    return deduped;
  };

  const handleSend = async () => {
    if (!selectedTemplate) { toast.error("Vui lòng chọn mẫu tin nhắn"); return; }
    const ids = getRecipientIds();
    if (ids.length === 0) { toast.error("Vui lòng chọn người nhận"); return; }
    setSending(true);
    try {
      const body = { template_id: selectedTemplate.id, customer_ids: ids, channel };
      const { data } = await api.post("/notifications/send", body);
      toast.success(data.message || `Đã gửi ${data.sent} thông báo`);
      loadLogs();
    } catch (err) { toast.error("Lỗi khi gửi"); }
    finally { setSending(false); }
  };

  const handleSendExpiring = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send-expiring");
      toast.success(`Đã nhắc ${data.sent} thành viên sắp hết hạn`);
      loadLogs();
    } catch (err) { toast.error("Lỗi khi gửi"); }
    finally { setSending(false); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Xóa mẫu tin nhắn này?")) return;
    try {
      await api.delete(`/notifications/templates/${id}`);
      toast.success("Đã xóa mẫu tin nhắn");
      loadData();
    } catch { toast.error("Lỗi khi xóa"); }
  };

  const toggleCustomer = (id) => {
    setSelectedCustomerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleImportPhones = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result || "";
      const phones = text.replace(/[,;\t]/g, "\n").split("\n").map(s => s.trim()).filter(s => /^0\d{8,10}$/.test(s));
      if (phones.length > 0) { setTPhones(phones.join("\n")); toast.success(`Đã import ${phones.length} số điện thoại`); }
      else { toast.error("Không tìm thấy số điện thoại hợp lệ trong file"); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleSendTournamentFee = async () => {
    const phones = tPhones.split("\n").map(s => s.trim()).filter(Boolean);
    if (phones.length === 0) { toast.error("Chưa có số điện thoại"); return; }
    if (!tContent) { toast.error("Chưa nhập nội dung thi đấu"); return; }
    if (!tFee) { toast.error("Chưa nhập lệ phí"); return; }
    setTSending(true);
    setTResult(null);
    try {
      const { data } = await api.post("/notifications/send-tournament-fee", {
        phones, tournament_content: tContent, tournament_fee: tFee,
        bank_account: tBankAccount, deadline: tDeadline, zns_template_id: tZnsTemplateId,
      });
      setTResult(data);
      toast.success(data.message || `Đã gửi ${data.sent || 0}/${phones.length}`);
      loadLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi gửi thông báo"); }
    finally { setTSending(false); }
  };

  const formatDeadlinePreview = (val) => {
    if (!val) return "<han_thanh_toan>";
    const d = new Date(val);
    return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")} - ${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
  };

  const filteredCustomers = (() => {
    let list = selectedSources.length > 0 ? filteredBySourceCustomers : customers;
    if (searchQ) {
      list = list.filter(c => c.name.toLowerCase().includes(searchQ.toLowerCase()) || (c.phone || "").includes(searchQ));
    }
    return list;
  })();

  const tabs = [
    { key: "send", label: "Gửi thông báo" },
    { key: "tournament", label: "CRM giải đấu" },
    { key: "pickleague", label: "Giải Pickleague" },
    { key: "logs", label: "Lịch sử gửi" },
    { key: "templates", label: "Mẫu tin nhắn" },
    { key: "zns_vars", label: "Biến ZNS" },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight">Thông báo</h1>
          <p className="text-slate-400 text-sm mt-1">Gửi Zalo/SMS cho khách hàng</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSendExpiring} disabled={sending} variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 gap-1.5 text-xs" data-testid="send-expiring-btn">
            <Bell className="w-3.5 h-3.5" /> Nhắc sắp hết hạn
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0f172a] border border-[#334155] rounded-lg p-1" data-testid="notification-tabs">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === t.key ? "bg-[#1e293b] text-lime-400" : "text-slate-400 hover:text-slate-200"}`}
            data-testid={`tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {/* Send Tab */}
      {activeTab === "send" && (
        <div className="space-y-4" data-testid="tab-content-send">
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4 space-y-4">
            {/* Channel */}
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Kênh</label>
              <div className="flex gap-2">
                {["zalo", "sms"].map(ch => (
                  <button key={ch} onClick={() => setChannel(ch)}
                    className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${channel === ch ? "bg-lime-500/20 text-lime-400 border-lime-500/30" : "bg-transparent text-slate-400 border-[#334155]"}`}>
                    {ch === "zalo" ? "Zalo" : "SMS"}
                  </button>
                ))}
              </div>
            </div>

            {/* Template */}
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Mẫu tin nhắn</label>
              <Select value={selectedTemplate?.id || ""} onValueChange={(v) => setSelectedTemplate(templates.find(t => t.id === v) || null)}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="template-select">
                  <SelectValue placeholder="Chọn mẫu..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  {templates.filter(t => t.is_active).map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-slate-300">{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview */}
            {selectedTemplate && (
              <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Xem trước:</p>
                <p className="text-sm text-slate-300 whitespace-pre-wrap">
                  {selectedTemplate.content
                    .replace(/\{customer_name\}/g, "Nguyễn Văn An")
                    .replace(/\{order_code\}/g, "#00001230426")
                    .replace(/\{payment_status\}/g, "Đã thanh toán")
                    .replace(/\{end_date\}/g, "2026-05-01")
                    .replace(/\{group_name\}/g, "Hội Pickleball Sáng")
                    .replace(/\{booking_date\}/g, "2026-04-22")
                    .replace(/\{court_name\}/g, "Sân 1")
                    .replace(/\{start_time\}/g, "17:00")
                    .replace(/\{end_time\}/g, "19:00")
                    .replace(/\{customer_phone\}/g, "0901234567")
                    .replace(/\{join_date\}/g, "2026-04-01")
                    .replace(/\{months_paid\}/g, "3")
                    .replace(/\{total_price\}/g, "500.000đ")}
                </p>
              </div>
            )}

            {/* Recipients */}
            <div className="space-y-3">
              <label className="text-xs text-slate-400 block">Chọn người nhận</label>

              {/* Source filter dropdown - always visible */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 shrink-0">Lọc nguồn:</span>
                <div className="flex flex-wrap gap-1.5">
                  {[...allSources, "Giải đấu"].filter((v, i, a) => a.indexOf(v) === i).map(s => {
                    const count = customers.filter(c => c.source === s).length;
                    if (count === 0) return null;
                    const isSelected = selectedSources.includes(s);
                    return (
                      <button key={s} onClick={() => setSelectedSources(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                        className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors flex items-center gap-1 ${isSelected ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "text-slate-500 border-[#334155] hover:text-slate-300 hover:border-slate-400"}`}
                        data-testid={`source-filter-${s}`}>
                        {isSelected && <CheckCircle2 className="w-2.5 h-2.5" />}
                        {getSourceLabel(s)} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Recipient mode buttons - multi-select */}
              <div className="space-y-1.5">
                <p className="text-[10px] text-slate-600 uppercase tracking-wider font-medium">Theo hội viên</p>
                {[
                  { key: "all", label: `Tất cả khách hàng (${filteredBySourceCustomers.length})` },
                  { key: "expiring", label: `Thành viên sắp hết hạn (${filteredExpiringIds.length})` },
                  { key: "expired", label: `Thành viên quá hạn (${filteredExpiredIds.length})` },
                ].map(opt => (
                  <button key={opt.key}
                    onClick={() => toggleMode(opt.key)}
                    onDoubleClick={() => handleRecipientDoubleClick(opt.key)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm border transition-colors ${selectedModes.includes(opt.key) ? "bg-lime-500/10 text-lime-400 border-lime-500/30" : "text-slate-400 border-[#334155] hover:border-slate-400"}`}
                    data-testid={`recipient-${opt.key}`}>
                    <span>{opt.label}</span>
                    {selectedModes.includes(opt.key) && <span className="text-[10px] text-slate-600 ml-2">(double-click xem DS)</span>}
                  </button>
                ))}
                <p className="text-[10px] text-slate-600 uppercase tracking-wider font-medium mt-2">Theo trạng thái thanh toán</p>
                {[
                  { key: "paid", label: `Đã thanh toán (${filteredPaidIds.length})`, active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
                  { key: "unpaid", label: `Chưa thanh toán (${filteredUnpaidIds.length})`, active: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
                  { key: "overdue_pay", label: `Quá hạn thanh toán (${filteredOverdueIds.length})`, active: "bg-red-500/10 text-red-400 border-red-500/30" },
                ].map(opt => (
                  <button key={opt.key}
                    onClick={() => toggleMode(opt.key)}
                    onDoubleClick={() => handleRecipientDoubleClick(opt.key)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm border transition-colors ${selectedModes.includes(opt.key) ? opt.active : "text-slate-400 border-[#334155] hover:border-slate-400"}`}
                    data-testid={`recipient-${opt.key}`}>
                    <span>{opt.label}</span>
                    {selectedModes.includes(opt.key) && <span className="text-[10px] text-slate-600 ml-2">(double-click xem DS)</span>}
                  </button>
                ))}
                <p className="text-[10px] text-slate-600 uppercase tracking-wider font-medium mt-2">Khác</p>
                <button
                  onClick={() => toggleMode("manual")}
                  onDoubleClick={() => handleRecipientDoubleClick("manual")}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm border transition-colors ${selectedModes.includes("manual") ? "bg-lime-500/10 text-lime-400 border-lime-500/30" : "text-slate-400 border-[#334155] hover:border-slate-400"}`}
                  data-testid="recipient-manual">
                  Chọn thủ công
                </button>
              </div>

              {/* Info: selected sources */}
              {selectedSources.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/5 border border-amber-500/10 rounded-md px-3 py-1.5">
                  <span>Đang lọc {selectedSources.length} nguồn: {selectedSources.map(s => getSourceLabel(s)).join(", ")}</span>
                  <button onClick={() => setSelectedSources([])} className="text-slate-500 hover:text-slate-300 underline ml-auto shrink-0">Bỏ lọc</button>
                </div>
              )}
            </div>

            {/* Manual customer selection */}
            {selectedModes.includes("manual") && (
              <div className="space-y-2">
                <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Tìm khách hàng..."
                  className="bg-[#0f172a] border-[#334155] text-slate-200 h-8 text-xs" />
                <div className="max-h-[200px] overflow-y-auto space-y-1">
                  {filteredCustomers.map(c => (
                    <button key={c.id} onClick={() => toggleCustomer(c.id)}
                      className={`w-full flex items-center justify-between px-3 py-1.5 rounded text-xs transition-colors ${selectedCustomerIds.includes(c.id) ? "bg-lime-500/10 text-lime-400" : "text-slate-400 hover:bg-[#334155]/30"}`}>
                      <span>{c.name} {c.phone ? `(${c.phone})` : ""}</span>
                      {selectedCustomerIds.includes(c.id) && <CheckCircle2 className="w-3.5 h-3.5" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleDraftPreview} disabled={draftLoading || !selectedTemplate || selectedModes.length === 0}
                variant="outline" className="flex-1 border-[#334155] text-slate-300 hover:bg-[#334155] gap-1.5" data-testid="draft-preview-btn">
                <Eye className="w-4 h-4" /> {draftLoading ? "Đang tạo..." : "Xem nháp"}
              </Button>
              <Button onClick={handleSend} disabled={sending || !selectedTemplate || selectedModes.length === 0}
                className="flex-1 bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold gap-1.5" data-testid="send-btn">
                <Send className="w-4 h-4" /> {sending ? "Đang gửi..." : `Gửi (${getRecipientIds().length})`}
              </Button>
            </div>
          </div>

          <div className={`border rounded-lg p-3 text-xs flex items-start gap-2 ${
            channel === "zalo" && zaloStatus.connected && zaloStatus.hasTemplate
              ? "bg-lime-500/10 border-lime-500/20 text-lime-400"
              : channel === "zalo" && zaloStatus.connected
              ? "bg-amber-500/10 border-amber-500/20 text-amber-400"
              : "bg-amber-500/10 border-amber-500/20 text-amber-400"
          }`} data-testid="notification-status-banner">
            {channel === "zalo" && zaloStatus.connected && zaloStatus.hasTemplate ? (
              <><CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Zalo ZNS sẵn sàng — Tin nhắn sẽ được gửi thật qua Zalo.</>
            ) : channel === "zalo" && zaloStatus.connected ? (
              <><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Zalo đã kết nối nhưng chưa có ZNS Template ID — Đang ở chế độ MOCK. Cấu hình trong Cài đặt.</>
            ) : channel === "sms" ? (
              <><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> SMS đang ở chế độ MOCK — Cấu hình SMS API trong Cài đặt để gửi thật.</>
            ) : (
              <><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Chưa kết nối Zalo — Đang ở chế độ MOCK. Cấu hình API key trong Cài đặt.</>
            )}
          </div>
        </div>
      )}


      {/* CRM giải đấu Tab */}
      {activeTab === "tournament" && <TournamentCRM />}

      {activeTab === "pickleague" && <PickleagueTab />}

      {/* Logs Tab */}
      {activeTab === "logs" && (
        <div className="space-y-4" data-testid="tab-content-logs">
          {/* Stats - clickable filters */}
          <div className="flex flex-wrap gap-3">
            {[
              { key: "", label: "Tất cả", value: logStats.total_sent + logStats.total_failed, icon: <Users className="w-4 h-4" />, clr: "sky" },
              { key: "sent", label: "Đã gửi", value: logStats.total_sent, icon: <CheckCircle2 className="w-4 h-4" />, clr: "lime" },
              { key: "failed", label: "Thất bại", value: logStats.total_failed, icon: <AlertTriangle className="w-4 h-4" />, clr: "red" },
              { key: "mock", label: "MOCK", value: null, icon: <Clock className="w-4 h-4" />, clr: "slate" },
            ].map(s => {
              const isActive = logFilterStatus === s.key;
              const colorMap = { sky: { bg: "bg-sky-500/15", border: "border-sky-500/30", text: "text-sky-400" }, lime: { bg: "bg-lime-500/15", border: "border-lime-500/30", text: "text-lime-400" }, red: { bg: "bg-red-500/15", border: "border-red-500/30", text: "text-red-400" }, slate: { bg: "bg-slate-500/15", border: "border-slate-500/30", text: "text-slate-400" } };
              const c = colorMap[s.clr];
              return (
                <button key={s.key} onClick={() => { setLogFilterStatus(prev => prev === s.key ? "" : s.key); setLogPage(1); setLogs([]); logPageRef.current = 1; hasMoreRef.current = false; }}
                  className={`rounded-lg px-4 py-2 flex items-center gap-2 border transition-all cursor-pointer ${isActive ? `${c.bg} ${c.border} ${c.text}` : "bg-[#1e293b] border-[#334155] text-slate-400 hover:border-slate-400"}`}
                  data-testid={`log-status-${s.key || "all"}`}>
                  <span className={isActive ? c.text : "text-slate-500"}>{s.icon}</span>
                  <span className="text-xs">{s.label}: <span className={`font-bold ${isActive ? c.text : c.text}`}>{s.value !== null ? s.value : "—"}</span></span>
                </button>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <Select value={logFilterSource || "all"} onValueChange={(v) => { setLogFilterSource(v === "all" ? "" : v); setLogPage(1); setLogs([]); logPageRef.current = 1; hasMoreRef.current = false; }}>
              <SelectTrigger className="w-[180px] bg-[#1e293b] border-[#334155] text-slate-300 h-8 text-xs" data-testid="log-filter-source">
                <SelectValue placeholder="Tất cả nguồn" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e293b] border-[#334155]">
                <SelectItem value="all" className="text-slate-300">Tất cả nguồn</SelectItem>
                {logStats.sources.map(s => (
                  <SelectItem key={s} value={s} className="text-slate-300">{getSourceLabel(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={logFilterZns || "all"} onValueChange={(v) => { setLogFilterZns(v === "all" ? "" : v); setLogPage(1); setLogs([]); logPageRef.current = 1; hasMoreRef.current = false; }}>
              <SelectTrigger className="w-[180px] bg-[#1e293b] border-[#334155] text-slate-300 h-8 text-xs" data-testid="log-filter-zns">
                <SelectValue placeholder="Tất cả ZNS ID" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e293b] border-[#334155]">
                <SelectItem value="all" className="text-slate-300">Tất cả ZNS ID</SelectItem>
                {logStats.zns_template_ids.map(z => (
                  <SelectItem key={z} value={z} className="text-slate-300">ZNS #{z}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155] hover:bg-transparent">
                  <TableHead className="text-slate-400">Thời gian</TableHead>
                  <TableHead className="text-slate-400">Kênh</TableHead>
                  <TableHead className="text-slate-400">Khách hàng</TableHead>
                  <TableHead className="text-slate-400">Nguồn</TableHead>
                  <TableHead className="text-slate-400">ZNS ID</TableHead>
                  <TableHead className="text-slate-400">Nội dung</TableHead>
                  <TableHead className="text-slate-400">Trạng thái</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow className="border-[#334155]">
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">Chưa có lịch sử gửi</TableCell>
                  </TableRow>
                ) : logs.map(log => (
                  <TableRow key={log.id} className="border-[#334155] hover:bg-[#334155]/20">
                    <TableCell className="text-slate-400 text-xs">{log.sent_at ? new Date(log.sent_at).toLocaleString("vi-VN") : ""}</TableCell>
                    <TableCell>
                      <Badge className={`${log.channel === "zalo" || log.channel === "zalo_zns" || log.channel === "ZALO_ZNS" ? "bg-sky-500/20 text-sky-400 border-sky-500/30" : log.channel === "mock" ? "bg-slate-500/20 text-slate-400 border-slate-500/30" : "bg-purple-500/20 text-purple-400 border-purple-500/30"} text-[10px] px-1.5 py-0 border`}>
                        {log.channel?.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">{log.customer_name || log.customer_phone}</TableCell>
                    <TableCell>
                      {log.customer_source ? (
                        <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0 border">{getSourceLabel(log.customer_source)}</Badge>
                      ) : <span className="text-slate-600 text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-slate-400 text-xs font-mono">{log.zns_template_id || "—"}</TableCell>
                    <TableCell className="text-slate-400 text-xs max-w-[180px] truncate">{log.content}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={`${log.status === "sent" ? "bg-lime-500/20 text-lime-400 border-lime-500/30" : log.status === "failed" ? "bg-red-500/20 text-red-400 border-red-500/30" : "bg-slate-500/20 text-slate-400 border-slate-500/30"} text-[10px] px-1.5 py-0 border`}>
                          {log.status === "sent" ? "Đã gửi" : log.status === "failed" ? "Thất bại" : log.status === "mock" ? "MOCK" : log.status}
                        </Badge>
                        <span role="button" tabIndex={0}
                          onClick={() => { viewZnsLogDetail(log.id); }}
                          style={{ cursor: "pointer", display: "inline-flex", padding: 2 }}
                          className="text-cyan-400/60 hover:text-cyan-400" data-testid={`view-log-${log.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </span>
                      </div>
                      {log.error_detail && (
                        <p className="text-[9px] text-red-400/70 mt-0.5 max-w-[200px] truncate" title={log.error_detail}>
                          {log.error_detail}
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div ref={logsEndRef} className="flex justify-center p-3 border-t border-[#334155]">
              {logsLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
              ) : logs.length > 0 && logs.length >= logTotal ? (
                <span className="text-[10px] text-slate-600">Hiển thị tất cả {logTotal} tin nhắn</span>
              ) : logs.length > 0 ? (
                <span className="text-[10px] text-slate-600">Kéo xuống để tải thêm ({logs.length}/{logTotal})</span>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === "templates" && (
        <div className="space-y-4" data-testid="tab-content-templates">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingTmpl(null); setTmplModalOpen(true); }} className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold gap-1.5 text-xs" data-testid="add-template-btn">
              <Plus className="w-3.5 h-3.5" /> Tạo mẫu
            </Button>
          </div>
          <div className="space-y-3">
            {templates.map(t => (
              <div key={t.id} className="bg-[#1e293b] border border-[#334155] rounded-lg p-4" data-testid={`template-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-[#F8FAFC]">{t.name}</h3>
                      <Badge className={`${t.type === "zalo" ? "bg-sky-500/20 text-sky-400 border-sky-500/30" : "bg-purple-500/20 text-purple-400 border-purple-500/30"} text-[10px] px-1.5 py-0 border`}>
                        {t.type?.toUpperCase()}
                      </Badge>
                      {t.zns_template_id && (
                        <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0 border font-mono">
                          ZNS #{t.zns_template_id}
                        </Badge>
                      )}
                      {!t.is_active && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] px-1.5 py-0 border">Tắt</Badge>}
                    </div>
                    <p className="text-xs text-slate-400 mt-2 whitespace-pre-wrap">{t.content}</p>
                  </div>
                  <div className="flex gap-1 shrink-0 ml-3">
                    <button onClick={() => { setEditingTmpl(t); setTmplModalOpen(true); }} className="p-1.5 rounded text-slate-400 hover:text-lime-400"><Edit className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDeleteTemplate(t.id)} className="p-1.5 rounded text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <p className="text-[10px] text-slate-600 mt-2">Biến: {(t.content.match(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g) || []).join(", ") || "Không có biến"}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <TemplateModal open={tmplModalOpen} onOpenChange={setTmplModalOpen} template={editingTmpl} onSaved={loadData} />

      {/* Draft Preview Dialog */}
      <Dialog open={draftOpen} onOpenChange={setDraftOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-xl max-h-[80vh] overflow-y-auto" data-testid="draft-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <Eye className="w-4 h-4 text-sky-400" />
              Xem nháp tin nhắn ({draftPreviews.length} người)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {draftPreviews.map((p, i) => (
              <div key={p.customer_id} className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-600 font-mono">#{i + 1}</span>
                    <span className="text-sm text-slate-200 font-medium">{p.name}</span>
                    <span className="text-xs text-slate-500">{p.phone}</span>
                  </div>
                  <Badge className={`text-[9px] px-1.5 py-0 border ${
                    p.payment_status === "Đã thanh toán" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" :
                    p.payment_status === "Quá hạn" ? "bg-red-500/15 text-red-400 border-red-500/20" :
                    p.payment_status === "Chưa thanh toán" ? "bg-amber-500/15 text-amber-400 border-amber-500/20" :
                    "bg-slate-500/15 text-slate-400 border-slate-500/20"
                  }`}>{p.payment_status}</Badge>
                </div>
                <div className="bg-[#1e293b] rounded-md p-2.5 border border-[#334155]/50">
                  <p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{p.message}</p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDraftOpen(false)} className="border-[#334155] text-slate-300 hover:bg-[#334155]">Đóng</Button>
            <Button onClick={() => { setDraftOpen(false); handleSend(); }} disabled={sending}
              className="bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400 gap-1">
              <Send className="w-3.5 h-3.5" /> Xác nhận gửi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipient Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md max-h-[70vh] overflow-y-auto" data-testid="recipient-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <Users className="w-4 h-4 text-lime-400" />
              Danh sách người nhận ({previewRecipients.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {previewRecipients.map((c, i) => (
              <div key={c.id} className="flex items-center gap-3 px-3 py-1.5 rounded bg-[#0f172a]/50 text-sm">
                <span className="text-[10px] text-slate-600 font-mono w-6">{i + 1}</span>
                <span className="text-slate-200 font-medium flex-1 truncate">{c.name}</span>
                <span className="text-slate-500 text-xs shrink-0">{c.phone || "—"}</span>
                {c.payment_status === "paid" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                {c.payment_status === "overdue" && <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-[#334155] text-slate-300 hover:bg-[#334155]">Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ZNS Variables Tab */}
      {activeTab === "zns_vars" && <ZNSVariablesTab />}

      {/* ZNS Log Detail Dialog */}
      <Dialog open={!!znsLogDetail} onOpenChange={() => setZnsLogDetail(null)}>
        <DialogContent className="bg-[#0f172a] border-[#334155] sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              <Eye className="w-4 h-4 text-cyan-400" />
              Chi tiết Log gửi ZNS
            </DialogTitle>
          </DialogHeader>
          {znsLogLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cyan-400 mx-auto" /></div>
          ) : znsLogDetail ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge className={`${znsLogDetail.status === "sent" ? "bg-lime-500/20 text-lime-400 border-lime-500/30" : znsLogDetail.status === "failed" ? "bg-red-500/20 text-red-400 border-red-500/30" : "bg-slate-500/20 text-slate-400"} text-xs px-2 py-0.5 border`}>
                  {znsLogDetail.status === "sent" ? "THÀNH CÔNG" : "THẤT BẠI"}
                </Badge>
                <span className="text-xs text-slate-400">{znsLogDetail.sent_at ? new Date(znsLogDetail.sent_at).toLocaleString("vi-VN") : ""}</span>
              </div>
              <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Khách hàng:</span> <span className="text-slate-200">{znsLogDetail.customer_name}</span></div>
                  <div><span className="text-slate-500">SĐT:</span> <span className="text-cyan-400 font-mono">{znsLogDetail.customer_phone || "—"}</span></div>
                  <div><span className="text-slate-500">Template ID:</span> <span className="text-lime-400 font-mono">{znsLogDetail.zns_template_id}</span></div>
                  <div><span className="text-slate-500">Nguồn:</span> <span className="text-slate-300">{znsLogDetail.customer_source || "—"}</span></div>
                </div>
                <div className="text-xs"><span className="text-slate-500">Nội dung:</span></div>
                <p className="text-xs text-slate-300 bg-[#0f172a] rounded p-2 whitespace-pre-wrap">{znsLogDetail.content}</p>
              </div>
              {znsLogDetail.error_detail && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-xs font-bold text-red-400">Phân tích lỗi</span>
                  </div>
                  <p className="text-xs text-red-300">{znsLogDetail.error_detail}</p>
                </div>
              )}
              {znsLogDetail.template_data && (
                <div className="bg-[#1e293b] border border-cyan-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Send className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="text-xs font-bold text-cyan-400">Dữ liệu gửi đi (Template Variables)</span>
                  </div>
                  <div className="space-y-1">
                    {Object.entries(znsLogDetail.template_data).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-xs">
                        <code className="text-purple-400 bg-purple-500/10 px-1 py-0.5 rounded text-[10px]">{`<${k}>`}</code>
                        <span className="text-slate-300">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {znsLogDetail.zns_response && (
                <div className="bg-[#1e293b] border border-amber-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs font-bold text-amber-400">Phản hồi từ Zalo ZNS (Raw)</span>
                  </div>
                  <pre className="text-[10px] text-slate-300 bg-[#0f172a] rounded p-2 overflow-x-auto whitespace-pre-wrap font-mono">
                    {JSON.stringify(znsLogDetail.zns_response, null, 2)}
                  </pre>
                </div>
              )}
              {!znsLogDetail.zns_response && !znsLogDetail.template_data && (
                <div className="text-center py-4 text-xs text-slate-500">
                  Log cũ — chưa lưu chi tiết response.<br />Các log mới sẽ hiển thị đầy đủ thông tin.
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setZnsLogDetail(null)} className="text-slate-400">Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateModal({ open, onOpenChange, template, onSaved }) {
  const isEdit = !!template;
  const [form, setForm] = useState({ name: "", type: "zalo", content: "", is_active: true, zns_template_id: "", variable_mapping: {} });
  const [saving, setSaving] = useState(false);

  // App parameters available for mapping
  const APP_PARAMS = [
    { key: "", label: "— Chọn —" },
    { key: "customer_name", label: "Tên khách hàng" },
    { key: "customer_phone", label: "SĐT khách hàng" },
    { key: "order_code", label: "Mã đơn hàng tự động" },
    { key: "payment_status", label: "Trạng thái thanh toán" },
    { key: "end_date", label: "Ngày hết hạn" },
    { key: "group_name", label: "Tên hội" },
    { key: "join_date", label: "Ngày tham gia" },
    { key: "months_paid", label: "Số tháng đã đóng" },
    { key: "booking_date", label: "Ngày đặt sân" },
    { key: "court_name", label: "Tên sân" },
    { key: "start_time", label: "Giờ bắt đầu" },
    { key: "end_time", label: "Giờ kết thúc" },
    { key: "total_price", label: "Tổng tiền" },
  ];

  useEffect(() => {
    if (open) {
      if (template) {
        setForm({ name: template.name, type: template.type, content: template.content, is_active: template.is_active, zns_template_id: template.zns_template_id || "", variable_mapping: template.variable_mapping || {} });
      } else {
        setForm({ name: "", type: "zalo", content: "", is_active: true, zns_template_id: "", variable_mapping: {} });
      }
    }
  }, [open, template]);

  // Extract variables from content: {var_name}
  const extractedVars = [...new Set((form.content.match(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g) || []).map(v => v.slice(1, -1)))];

  const setMapping = (varName, paramKey) => {
    setForm(f => ({ ...f, variable_mapping: { ...f.variable_mapping, [varName]: paramKey } }));
  };

  const insertVar = (varName) => {
    setForm(f => ({ ...f, content: f.content + `{${varName}}` }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.content.trim()) { toast.error("Vui lòng nhập tên và nội dung"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/notifications/templates/${template.id}`, form);
        toast.success("Đã cập nhật mẫu");
      } else {
        await api.post("/notifications/templates", form);
        toast.success("Đã tạo mẫu");
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) { toast.error("Lỗi"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="template-modal">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading">{isEdit ? "Sửa mẫu" : "Tạo mẫu mới"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tên mẫu *</label>
            <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="template-name-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Loại</label>
            <div className="flex gap-2">
              {["zalo", "sms"].map(t => (
                <button key={t} type="button" onClick={() => setForm(f => ({ ...f, type: t }))}
                  className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors ${form.type === t ? "bg-lime-500/20 text-lime-400 border-lime-500/30" : "bg-transparent text-slate-400 border-[#334155]"}`}>
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Nội dung *</label>
            <Textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder={"Xin chào {customer_name}..."}
              className="bg-[#0f172a] border-[#334155] text-slate-200 min-h-[100px]" data-testid="template-content-input" />
            <div className="flex flex-wrap gap-1 mt-1.5">
              <span className="text-[10px] text-slate-600">Chèn biến:</span>
              {APP_PARAMS.filter(p => p.key).map(p => (
                <button key={p.key} type="button" onClick={() => insertVar(p.key)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-[#0f172a] border border-[#334155] text-slate-500 hover:text-amber-400 hover:border-amber-500/30 transition-colors font-mono">
                  {"{" + p.key + "}"}
                </button>
              ))}
            </div>
          </div>

          {/* Variable Mapping */}
          {extractedVars.length > 0 && (
            <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
              <label className="text-xs text-slate-400 font-medium block">Tùy chỉnh biến</label>
              <div className="space-y-1.5">
                {extractedVars.map(v => (
                  <div key={v} className="flex items-center gap-2">
                    <span className="text-xs font-mono text-amber-400 bg-[#1e293b] px-2 py-1 rounded border border-[#334155] w-[140px] shrink-0 truncate" title={v}>
                      {"{" + v + "}"}
                    </span>
                    <span className="text-slate-600 text-xs shrink-0">=</span>
                    <select
                      value={form.variable_mapping[v] || v}
                      onChange={(e) => setMapping(v, e.target.value)}
                      className="flex-1 bg-[#1e293b] border border-[#334155] text-slate-200 rounded px-2 py-1 text-xs focus:border-lime-500 outline-none"
                      data-testid={`var-map-${v}`}
                    >
                      {APP_PARAMS.map(p => (
                        <option key={p.key} value={p.key}>{p.label}{p.key ? ` (${p.key})` : ""}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Zalo ZNS Template ID</label>
            <Input value={form.zns_template_id} onChange={(e) => setForm(f => ({ ...f, zns_template_id: e.target.value }))}
              placeholder="VD: 571057 (để trống nếu dùng MOCK)"
              className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="template-zns-id-input" />
            <p className="text-[10px] text-slate-600 mt-1">Gán ZNS Template ID từ Zalo Dashboard để gửi thật qua Zalo</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Hủy</Button>
            <Button type="submit" disabled={saving} className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold" data-testid="template-save-btn">
              {saving ? "Đang lưu..." : (isEdit ? "Cập nhật" : "Tạo")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// --- ZNS Variables Management Tab ---
function ZNSVariablesTab() {
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [form, setForm] = useState({ name: "", sample_value: "", description: "" });
  const [pinOpen, setPinOpen] = useState(false);
  const [pinAction, setPinAction] = useState(null); // "edit" | "delete"

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/zns-variables");
      setVariables(data.variables || []);
    } catch { toast.error("Lỗi tải biến ZNS"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSeed = async () => {
    try {
      const { data } = await api.post("/zns-variables/seed");
      toast.success(data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error("Tên biến không được trống"); return; }
    try {
      await api.post("/zns-variables", form);
      toast.success("Đã thêm biến");
      setAddOpen(false); setForm({ name: "", sample_value: "", description: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleEdit = async () => {
    if (!editItem) return;
    try {
      await api.put(`/zns-variables/${editItem.id}`, form);
      toast.success("Đã cập nhật");
      setEditItem(null); setPinOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/zns-variables/${deleteItem.id}`);
      toast.success("Đã xoá biến");
      setDeleteItem(null); setPinOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const onPinConfirm = () => {
    if (pinAction === "edit") handleEdit();
    else if (pinAction === "delete") handleDelete();
  };

  return (
    <div className="space-y-4" data-testid="zns-vars-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{variables.length} biến ZNS — Dùng trong mẫu tin nhắn Zalo ZNS</p>
        <div className="flex gap-2">
          {variables.length === 0 && (
            <Button size="sm" onClick={handleSeed} variant="outline" className="border-[#334155] text-slate-300 text-xs">Tạo mặc định</Button>
          )}
          <Button size="sm" onClick={() => { setForm({ name: "", sample_value: "", description: "" }); setAddOpen(true); }}
            className="bg-lime-500 text-[#0f172a] font-semibold text-xs" data-testid="add-zns-var-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Thêm biến
          </Button>
        </div>
      </div>

      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[#0f172a]">
            <tr className="text-slate-400">
              <th className="px-3 py-2 text-left">Tên biến</th>
              <th className="px-3 py-2 text-left">Giá trị mẫu</th>
              <th className="px-3 py-2 text-left">Mô tả</th>
              <th className="px-3 py-2 text-center w-24">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">Đang tải...</td></tr> :
            variables.length === 0 ? <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">Chưa có biến ZNS</td></tr> :
            variables.map(v => (
              <tr key={v.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10">
                <td className="px-3 py-2 font-mono text-teal-400">{`{{${v.name}}}`}</td>
                <td className="px-3 py-2 text-slate-300">{v.sample_value || "—"}</td>
                <td className="px-3 py-2 text-slate-500">{v.description || "—"}</td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => { setEditItem(v); setForm({ name: v.name, sample_value: v.sample_value, description: v.description }); setPinAction("edit"); setPinOpen(true); }}
                      className="text-slate-400 hover:text-lime-400 p-1"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => { setDeleteItem(v); setPinAction("delete"); setPinOpen(true); }}
                      className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-xs">
          <DialogHeader><DialogTitle className="text-[#F8FAFC] text-sm">Thêm biến ZNS</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Tên biến (VD: customer_name)"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9 font-mono" />
            <Input value={form.sample_value} onChange={e => setForm(f => ({...f, sample_value: e.target.value}))} placeholder="Giá trị mẫu"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" />
            <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Mô tả"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleAdd} className="bg-lime-500 text-[#0f172a] font-bold">Thêm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN for edit/delete */}
      <PinDialog open={pinOpen} onOpenChange={(v) => { setPinOpen(v); if (!v) { setEditItem(null); setDeleteItem(null); } }}
        onConfirm={onPinConfirm}
        title={pinAction === "delete" ? `Xoá biến "${deleteItem?.name}"` : `Sửa biến "${editItem?.name}"`}
        description={pinAction === "delete" ? "Nhập PIN để xác nhận xoá biến ZNS." : "Nhập PIN để xác nhận chỉnh sửa."} />

      {/* Edit form shows after PIN success - actually PIN confirms directly */}
    </div>
  );
}
